import React, { useState, useEffect } from "react";
import { MdClose } from "react-icons/md";
import axios from "axios";
import Swal from "sweetalert2";
import toast from "react-hot-toast";
import { uploadToCloudinary } from "../../../utils/cloudinary";
import { Loader2 } from "lucide-react";

const AdminSellerEditModal = ({ isOpen, onClose, seller, initialName, onSuccess }) => {
    const [loading, setLoading] = useState(false);

    const [form, setForm] = useState({
        fullName: "",
        mobNo: "",
        emailId: "",
        gender: "",
        address: "",
        businessName: "",
        businessType: "",
        businessRegistrationName: "",
        panNo: "",
        gstIn: "",
        commission: "",
        bulkOrderCommission: "",
        status: "",
        permissions: {},

        // Images
        shopImage: "",
        shopPreview: "",

        panImage: "",
        panPreview: "",

        gstCertificate: "",
        gstPreview: "",

        ownerIdProof: "",
        ownerPreview: "",

        addressProof: "",
        addressPreview: "",

        otherDocuments: [],
        otherDocsPreview: [],
    });

    // Extract proper Cloudinary URL
    const extractUrl = (result) => {
        if (!result) return "";
        if (typeof result === "string") return result;
        return result.secure_url || result.url || "";
    };

    // Fetch seller data
    useEffect(() => {
        if (seller) {
            setForm({
                ...form,
                fullName: seller.fullName || "",
                mobNo: seller.mobNo || "",
                emailId: seller.emailId || "",
                gender: seller.gender || "",
                address: seller.address || "",
                businessName: seller.businessName || "",
                businessType: seller.businessType || "",
                businessRegistrationName: seller.businessRegistrationName || "",
                panNo: seller.panNo || "",
                gstIn: seller.gstIn || "",
                commission: seller.commission || 5,
                bulkOrderCommission: seller.bulkOrderCommission || 0,
                status: "Approved",
                permissions: seller.permissions || {},

                shopImage: seller.shopImage || "",
                shopPreview: "",

                panImage: seller.panImage || "",
                panPreview: "",

                gstCertificate: seller.gstCertificate || "",
                gstPreview: "",

                ownerIdProof: seller.ownerIdProof || "",
                ownerPreview: "",

                addressProof: seller.addressProof || "",
                addressPreview: "",

                otherDocuments: seller.otherDocuments || [],
                otherDocsPreview: [],
            });
        } else {
            // Reset for Add Mode
            setForm({
                fullName: "",
                mobNo: "",
                emailId: "",
                gender: "",
                address: "",
                businessName: initialName || "",
                businessType: "",
                businessRegistrationName: "",
                panNo: "",
                gstIn: "",
                commission: 5,
                bulkOrderCommission: 0,
                status: "Approved",
                permissions: {},
                shopImage: "",
                shopPreview: "",
                panImage: "",
                panPreview: "",
                gstCertificate: "",
                gstPreview: "",
                ownerIdProof: "",
                ownerPreview: "",
                addressProof: "",
                addressPreview: "",
                otherDocuments: [],
                otherDocsPreview: [],
            });
        }
    }, [seller, initialName, isOpen]);

    if (!isOpen) return null;

    const handleChange = (e) => {
        setForm({ ...form, [e.target.name]: e.target.value });
    };

    // Handle file uploads with preview
    const handleFileChange = (e, field, previewField) => {
        const file = e.target.files[0];
        if (!file) return;

        const blob = URL.createObjectURL(file);

        setForm((prev) => ({
            ...prev,
            [field]: file,
            [previewField]: blob,
        }));
    };

    const handleSubmit = async () => {
        try {
            setLoading(true);

            let payload = { ...form };

            // Convert commission fields to number
            payload.commission = Number(payload.commission);
            payload.bulkOrderCommission = Number(payload.bulkOrderCommission);

            // Upload helper
            const handleUpload = async (file) => {
                if (file instanceof File) {
                    const res = await uploadToCloudinary(file);
                    return extractUrl(res);
                }
                return file;
            };

            // Upload all images
            payload.shopImage = await handleUpload(form.shopImage);
            payload.panImage = await handleUpload(form.panImage);
            payload.gstCertificate = await handleUpload(form.gstCertificate);
            payload.ownerIdProof = await handleUpload(form.ownerIdProof);
            payload.addressProof = await handleUpload(form.addressProof);

            // Multi docs upload
            const updatedDocs = [];
            for (const doc of form.otherDocuments) {
                updatedDocs.push(await handleUpload(doc));
            }
            payload.otherDocuments = updatedDocs;

            let response;
            if (seller) {
                response = await axios.put(
                    `${process.env.REACT_APP_API_URL}/supplier/update-suppliers/${seller._id}`,
                    payload
                );
                Swal.fire("Updated", "Seller updated successfully", "success");
            } else {
                response = await axios.post(
                    `${process.env.REACT_APP_API_URL}/supplier/suppliers/register`,
                    payload
                );
                Swal.fire("Created", "Seller created successfully", "success");
            }

            onSuccess && onSuccess(response.data.data);
            onClose();
        } catch (err) {
            console.error(err);
            toast.error(err?.response?.data?.message || "Update failed");
        } finally {
            setLoading(false);
        }
    };


    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
            <div className="bg-[#ecf1f5] w-full max-w-3xl p-6 rounded-xl shadow-lg relative overflow-y-auto max-h-[90vh]">
                {/* Close */}
                <button onClick={onClose} className="absolute right-3 top-3">
                    <MdClose size={24} />
                </button>

                <h2 className="text-2xl font-semibold text-center mb-3">
                    {seller ? "Edit Seller" : "Add Seller"}
                </h2>

                {/* FORM */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

                    {/* Full Name */}
                    <Input label="Full Name" name="fullName" value={form.fullName} onChange={handleChange} />

                    {/* Mobile */}
                    <Input label="Mobile No" name="mobNo" value={form.mobNo} onChange={handleChange} />

                    {/* Email */}
                    <Input label="Email" name="emailId" value={form.emailId} onChange={handleChange} />

                    {/* Gender */}
                    <div className="flex flex-col">
                        <label className="text-sm text-gray-600">Gender</label>
                        <select
                            name="gender"
                            value={form.gender}
                            onChange={handleChange}
                            className="border p-2 rounded-lg mt-1"
                        >
                            <option value="">Select Gender</option>
                            <option value="Male">Male</option>
                            <option value="Female">Female</option>
                            <option value="Other">Other</option>
                        </select>
                    </div>

                    {/* Address */}
                    <Input label="Address" name="address" value={form.address} onChange={handleChange} />

                    {/* PAN */}
                    <Input label="PAN No" name="panNo" value={form.panNo} onChange={handleChange} />

                    {/* GST */}
                    <Input label="GST IN" name="gstIn" value={form.gstIn} onChange={handleChange} />

                    {/* Business Name */}
                    <Input label="Business Name" name="businessName" value={form.businessName} onChange={handleChange} />

                    {/* Business Type */}
                    <Input label="Business Type" name="businessType" value={form.businessType} onChange={handleChange} />

                    {/* Registration Name */}
                    <Input
                        label="Business Registration Name"
                        name="businessRegistrationName"
                        value={form.businessRegistrationName}
                        onChange={handleChange}
                    />

                </div>

                {/* IMAGE UPLOADS */}
                <div className="grid md:grid-cols-2 gap-4 mt-4">
                    <ImageUploader
                        title="Shop Image"
                        file={form.shopImage}
                        preview={form.shopPreview}
                        url={form.shopImage}
                        onChange={(e) => handleFileChange(e, "shopImage", "shopPreview")}
                    />

                    <ImageUploader
                        title="PAN Image"
                        file={form.panImage}
                        preview={form.panPreview}
                        url={form.panImage}
                        onChange={(e) => handleFileChange(e, "panImage", "panPreview")}
                    />

                    <ImageUploader
                        title="GST Certificate"
                        file={form.gstCertificate}
                        preview={form.gstPreview}
                        url={form.gstCertificate}
                        onChange={(e) => handleFileChange(e, "gstCertificate", "gstPreview")}
                    />

                    <ImageUploader
                        title="Owner ID Proof"
                        file={form.ownerIdProof}
                        preview={form.ownerPreview}
                        url={form.ownerIdProof}
                        onChange={(e) => handleFileChange(e, "ownerIdProof", "ownerPreview")}
                    />

                    <ImageUploader
                        title="Address Proof"
                        file={form.addressProof}
                        preview={form.addressPreview}
                        url={form.addressProof}
                        onChange={(e) => handleFileChange(e, "addressProof", "addressPreview")}
                    />
                </div>

                {/* BUTTONS */}
                <div className="flex justify-end mt-6">
                    <button
                        onClick={handleSubmit}
                        className="bg-[#b07d17] px-6 py-2 rounded-lg text-white"
                    >
                        {loading ? <Loader2 className="animate-spin" /> : "Save Changes"}
                    </button>
                </div>
            </div>
        </div>
    );
};

// Reusable input component
const Input = ({ label, ...rest }) => (
    <div className="flex flex-col">
        <label className="text-sm text-gray-600">{label}</label>
        <input {...rest} className="border p-2 rounded-lg mt-1" />
    </div>
);

// Reusable image upload
const ImageUploader = ({ title, preview, url, onChange }) => (
    <div>
        <label className="text-sm text-gray-600">{title}</label>
        <input type="file" accept="image/*" onChange={onChange} className="mt-1" />
        {(preview || url) && (
            <img
                src={preview || url}
                alt={title}
                className="w-28 h-28 rounded-lg mt-2 object-cover"
            />
        )}
    </div>
);

export default AdminSellerEditModal;
